<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
      </div>
      <div class="modal__main-icon modal__main-icon--accident"
        v-if="policyFilesModalIsAccident || policyFilesModalIsAccidentChildren"></div>
      <div class="modal__main-icon modal__main-icon--policy" v-else></div>
      <div class="modal__close" @click="closeModal()"></div>

      <div class="modal__title" v-if="policyFilesModalIsTermination">
        {{ $t(`policyFilesModal.${"policyFiles"}`) }} (Termination)
      </div>
      <div class="modal__title" v-else-if="policyFilesModalIsPotential">
        {{ $t(`policyFilesModal.${"potentialPolicyFiles"}`) }}
      </div>
      <div class="modal__title" v-else-if="
        policyFilesModalIsAccident || policyFilesModalIsAccidentChildren
      ">
        {{ $t(`policyFilesModal.${"files"}`) }}
      </div>
      <div class="modal__title" v-else>{{ $t(`policyFilesModal.${"policyFiles"}`) }}</div>

      <div class="content">
        <div class="files">
          <div class="files__header">
            <div class="files__label files__label--large name">{{ $t(`policyFilesModal.${"name"}`) }}</div>
            <div class="files__label files__label--small">{{ $t(`policyFilesModal.${"view"}`) }}</div>
            <div class="files__label files__label--medium">{{ $t(`policyFilesModal.${"date"}`) }}</div>
            <div class="files__label files__label--medium">{{ $t(`policyFilesModal.${"documentType"}`) }}</div>
            <div class="files__label files__label--small">{{ $t(`policyFilesModal.${"public"}`) }}</div>
            <div class="files__label files__label--medium" v-if="!policyFilesModalIsTermination">
              {{ $t(`policyFilesModal.${"download"}`) }}
            </div>
            <div v-if="canManagePolicyFiles" class="files__label files__label--small">{{ $t(`buttons.${"deleteBtn"}`) }}</div>
          </div>
          <div class="files__body">
            <Loading v-if="filesLoading" />
            <template v-else>
              <File v-for="file in policyFiles" :key="file.id" :file="file" :updateFiles="updateFiles" :can-delete="canManagePolicyFiles" />
            </template>
          </div>
        </div>
        <PolicyDropFile v-if="canManagePolicyFiles" v-model="files" :document-types="uploadDocumentTypeOptions"
          @save="uploadFiles($event)" :deletable="true" :show-save-button="true" :disabled="!canManagePolicyFiles" />
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import api from "@/api";
import File from "@/components/AccountDetails/File";
import PolicyDropFile from "@/components/PolicyDropFile.vue";
import Loading from "@/partials/Loading";

defineOptions({
  name: "PolicyFilesModal"
});

const createEventBus = () => {
  const events = {};
  return {
    on(event, callback) {
      if (!events[event]) events[event] = [];
      events[event].push(callback);
    },
    emit(event, ...args) {
      if (events[event]) {
        events[event].forEach(callback => callback(...args));
      }
    },
    off(event, callback) {
      if (events[event]) {
        if (callback) {
          events[event] = events[event].filter(cb => cb !== callback);
        } else {
          events[event] = [];
        }
      }
    }
  };
};

const store = useStore();
const route = useRoute();

// Reactive Local State Data
const files = ref([]);
const bus = ref(createEventBus());
const filesLoading = ref(false);
const fetchedDocumentTypes = ref([]);

const fetchPolicyDocumentTypes = async () => {
  try {
    const response = await api({ requiresAuth: true }).get("/v1/partials/document-types", {
      params: { model: "policy" },
    });

    const data = Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data)
        ? response.data
        : [];

    fetchedDocumentTypes.value = data;
  } catch (error) {
    fetchedDocumentTypes.value = [];
  }
};

// Computed Mappings (Replaces mapGetters)
const policyFiles = computed(() => store.getters.policyFiles);
const policyFileId = computed(() => store.getters.policyFileId);
const customer = computed(() => store.getters.customer);
const policyFilesModalIsTermination = computed(() => store.getters.policyFilesModalIsTermination);
const policyFilesModalIsPotential = computed(() => store.getters.policyFilesModalIsPotential);
const policyFilesModalIsAccident = computed(() => store.getters.policyFilesModalIsAccident);
const policyFilesModalIsAccidentChildren = computed(() => store.getters.policyFilesModalIsAccidentChildren);
const filesFromDashboard = computed(() => store.getters.filesFromDashboard);
const customerFromDashboard = computed(() => store.getters.customerFromDashboard);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const policyDocumentTypes = computed(() => {
  const policyForms = store.getters.policyForms || {};
  return policyForms['policy-document-types']
    || policyForms['document-types']
    || [];
});

const policyDocumentTypeOptions = computed(() => {
  const items = [...fetchedDocumentTypes.value, ...policyDocumentTypes.value];

  return items.filter((item, index, list) => {
    const nextIndex = list.findIndex((candidate) => String(candidate.id) === String(item.id));
    return nextIndex === index;
  });
});
const supportsPolicyDocumentTypes = computed(() => !(
  policyFilesModalIsTermination.value
  || policyFilesModalIsPotential.value
  || policyFilesModalIsAccident.value
  || policyFilesModalIsAccidentChildren.value
));
const uploadDocumentTypeOptions = computed(() => (
  supportsPolicyDocumentTypes.value ? policyDocumentTypeOptions.value : []
));
const currentPolicyTeamId = computed(() => {
  const fallbackTeamId = customer.value?.team_id ?? customer.value?.team?.id ?? null;

  if (policyFilesModalIsPotential.value) {
    const potentialPolicy = customer.value?.potential_policies?.find((policy) => policy.id === policyFileId.value);
    return Number(potentialPolicy?.team_id ?? potentialPolicy?.team?.id ?? fallbackTeamId) || null;
  }

  if (policyFilesModalIsTermination.value) {
    const termination = customer.value?.terminations?.find((item) => item.id === policyFileId.value);
    return Number(termination?.team_id ?? termination?.team?.id ?? fallbackTeamId) || null;
  }

  if (policyFilesModalIsAccident.value || policyFilesModalIsAccidentChildren.value) {
    const accident = customer.value?.policies
      ?.map((policy) => policy.accidents)
      ?.flat()
      ?.find((item) => item.id === policyFileId.value);
    return Number(accident?.team_id ?? accident?.team?.id ?? fallbackTeamId) || null;
  }

  const policy = customer.value?.policies?.find((item) => item.id === policyFileId.value);
  return Number(policy?.team_id ?? policy?.team?.id ?? fallbackTeamId) || null;
});
const canManagePolicyFiles = computed(() => {
  if (isSuperAdmin.value) return true;
  if (!currentPolicyTeamId.value) return false;

  if (policyFilesModalIsPotential.value) {
    return store.getters.hasTeamPermission("potential_policy_create", currentPolicyTeamId.value);
  }

  return store.getters.hasTeamPermission("policy_create", currentPolicyTeamId.value);
});

onMounted(() => {
  fetchPolicyDocumentTypes();
});

// Lifecycle Cleanups
onUnmounted(() => {
  store.commit("setPolicyFilesModalIsTermination", false);
  store.commit("setPolicyFilesModalIsPotential", false);
  store.commit("setPolicyFilesModalIsAccident", false);
  store.commit("setPolicyFilesModalIsAccidentChildren", false);

  store.commit("setFilesFromDashboard", false);
  store.commit("setCustomerFromDashboard", null);
});

// Structural Interactor Actions
const closeModal = () => {
  store.commit("setShowPolicyFilesModal", false);
  store.commit("setFilesFromDashboard", false);
  store.commit("setCustomerFromDashboard", null);
};

const updateFiles = async () => {
  let customerId = null;
  if (filesFromDashboard.value) {
    customerId = customerFromDashboard.value;
  } else {
    customerId = route.params.id;
  }

  await store.dispatch("getGlobalCustomer", customerId);

  if (policyFilesModalIsTermination.value) {
    store.commit("setPolicyFiles", [
      ...customer.value.terminations.find(
        (termination) => termination.id === policyFileId.value
      ).termination_files,
    ]);
  } else if (policyFilesModalIsPotential.value) {
    store.commit("setPolicyFiles", [
      ...customer.value.potential_policies.find(
        (policy) => policy.id === policyFileId.value
      ).files,
    ]);

    if (route.path === '/dashboard') {
      try {
        await store.dispatch("getSalesDashboard");
      } catch (error) {
        // Fallback error logging boundary
      }
    }
  } else if (policyFilesModalIsAccident.value) {
    store.commit("setPolicyFiles", [
      ...customer.value.policies
        .map((policy) => policy.accidents)
        .flat()
        .find((accident) => accident.id === policyFileId.value)
        .accident_files,
      ...customer.value.policies
        .map((policy) => policy.accidents)
        .flat()
        .find((accident) => accident.id === policyFileId.value)
        .childrens.map((accident) => accident.accident_files)
        .flat(),
    ]);
  } else if (policyFilesModalIsAccidentChildren.value) {
    store.commit("setPolicyFiles", [
      ...customer.value.policies
        .map((policy) => policy.accidents)
        .flat()
        .map((accident) => accident.childrens)
        .flat()
        .find((accident) => accident.id === policyFileId.value)
        .accident_files,
    ]);
  } else {
    store.commit("setPolicyFiles", [
      ...customer.value.policies.find(
        (policy) => policy.id === policyFileId.value
      ).policy_files,
    ]);
  }
};

const uploadFiles = async (eventData, onComplete) => {
  if (!canManagePolicyFiles.value) {
    closeModal();
    onComplete?.();
    return;
  }

  filesLoading.value = true;
  files.value = eventData || [];

  const rawFiles = files.value.map((entry) => entry?.file || entry);
  const policyFilesToSubmit = files.value.map((entry) => ({
    file: entry?.file || entry,
    documentTypeId: entry?.documentTypeId ?? entry?.document_type_id ?? entry?.documentType?.id ?? null,
    is_public: Boolean(entry?.is_public ?? entry?.isPublic ?? false),
  }));

  try {
    if (policyFilesModalIsTermination.value) {
      await store.dispatch("uploadTerminationFiles", {
        terminationId: policyFileId.value,
        files: rawFiles,
      });
    } else if (policyFilesModalIsPotential.value) {
      await store.dispatch("addPotentialPolicyFile", {
        potentialPolicyId: policyFileId.value,
        files: rawFiles,
      });
    } else if (policyFilesModalIsAccident.value) {
      await store.dispatch("addAccidentFile", {
        accidentId: policyFileId.value,
        files: rawFiles,
      });
    } else if (policyFilesModalIsAccidentChildren.value) {
      await store.dispatch("addAccidentFile", {
        accidentId: policyFileId.value,
        files: rawFiles,
      });
    } else {
      const createdFilesResponse = await store.dispatch("addPolicyFile", {
        policyId: policyFileId.value,
        files: policyFilesToSubmit,
      });

      const createdFiles = Array.isArray(createdFilesResponse)
        ? createdFilesResponse
        : Array.isArray(createdFilesResponse?.data)
          ? createdFilesResponse.data
          : Array.isArray(createdFilesResponse?.files)
            ? createdFilesResponse.files
            : [];

      await Promise.all(
        (Array.isArray(files.value) ? files.value : []).map(async (fileEntry, index) => {
          const uuid = createdFiles[index]?.uuid || createdFiles[index]?.id || null;
          const documentTypeId = fileEntry?.documentTypeId ?? fileEntry?.document_type_id ?? fileEntry?.documentType?.id ?? null;

          if (uuid && documentTypeId !== null && documentTypeId !== undefined) {
            await store.dispatch("updateMediaDocumentType", {
              uuid,
              documentTypeId,
            });
          }
        })
      );
    }

    // Only update customer data if we're on a customer detail page
    // Skip for dashboard/list views to avoid unnecessary API calls
    if (!filesFromDashboard.value) {
      await updateFiles();
    }
    bus.value.emit("delete");

    // Close modal after successful upload
    closeModal();
  } finally {
    filesLoading.value = false;
    onComplete?.();
  }
};
</script>

<style lang="scss" scoped>
.modal {
  .modal__panel {
    width: min(900px, calc(100vw - 32px));
    height: min(600px, calc(100vh - 32px));
    height: min(600px, calc(100dvh - 32px));
    max-height: calc(100vh - 32px);
    max-height: calc(100dvh - 32px);
  }

  &__title {
    margin: 53px 0 35px 10px;
    font-size: var(--medium);
    font-family: var(--muli-text-semi-bold);
  }

  .modal__title,
  .files__label,
  :deep(.file) {
    color: var(--text-2);
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    height: calc(100% - 53px - 35px - 18px);
    gap: 16px;

    overflow: auto !important;
    min-height: 0;
  }

  .files__body {
    max-height: 195px; 
  }

  .files {
    width: 100%;
    height: auto;
    font-size: var(--x-small);

    &__header {
      border-bottom: 1px solid var(--border-10);
      display: flex;
      padding: 5px 10px;
      margin-bottom: 8px;
    }

    &__body {
      height: auto;
      overflow: auto;
      position: relative;
    }

    :deep(.file) {
      display: flex;
      padding: 20px 10px;
      background: var(--bg-3);
      margin-bottom: 1px;
      position: relative;
      height: 64px;
      max-height: 64px;
      transition: all 0.3s ease;

      .file__data {
        &--large {
          color: var(--text-10);
          text-decoration: underline;
        }
      }

      .icon {
        background-position: left;
      }
    }

    &__label,
    :deep(.file__data) {
      display: flex;
      align-items: center;
    }

    &__label--large,
    :deep(.file__data--large) {
      flex: 3;
    }

    &__label--medium,
    :deep(.file__data--medium) {
      flex: 2;
    }

    &__label--small,
    :deep(.file__data--small) {
      flex: 1;
    }
  }
}

@media (max-width: 640px) {
  .modal {
    padding: 8px;

    .modal__panel {
      width: calc(100vw - 16px);
      height: calc(100vh - 16px);
      height: calc(100dvh - 16px);
      max-height: calc(100vh - 16px);
      max-height: calc(100dvh - 16px);
      padding: 24px 12px 16px;
      border-radius: 12px;
    }

    &__title {
      margin: 42px 36px 20px 0;
      font-size: var(--small);
    }

    .content {
      height: calc(100% - 62px);
      gap: 12px;
    }

    .files {
      overflow-x: auto;

      &__header,
      :deep(.files__body .file) {
        min-width: 720px;
      }
    }
  }
}

.drop {
  min-height: 200px !important;
}


.modal .files__label {
  justify-content: center !important;
}

.modal .name {
  justify-content: flex-start !important;
}
</style>
